import React, {Component, ReactElement, RefObject} from 'react'

import NatAdminUserRepo, {UpdateAdminUserPasswordParams} from './repository/NatAdminUserRepo'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {Nullable} from '../../../app/TypeHelpers'
import {NatGetAdminUser} from './repository/NatGetAdminUser'
import {NatPostAdminUser} from './repository/NatPostAdminUser'
import {Messages} from '../../../Messages'
import styled from 'styled-components'
import {Colors} from '../../../styledComponents/Constants'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {FlexGridArea, ListGridContainer} from '../../../styledComponents/GridComponents'
import {AppAdminContext} from '../../../app/AppAdminContext'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {HeadingAreaMessageComponent} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Box} from '@mui/material'
import {Location} from 'react-router-dom'

interface Props {
    adminUserRepo: NatAdminUserRepo
    location: Location
}

interface State {
    password: string
    passwordConfirm: string
    version: number
    isLoadCompleted: boolean
}

class NatAdminChangePasswordInputPage extends Component<Props & AdminPagePropsSet, State> {
    declare context: React.ContextType<typeof AppAdminContext>

    password: RefObject<HTMLInputElement>
    passwordConfirm: RefObject<HTMLInputElement>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            password: '',
            passwordConfirm: '',
            version: 1,
            isLoadCompleted: false,
        }

        this.password = React.createRef()
        this.passwordConfirm = React.createRef()


    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        // パスワード完了後の遷移先URLが未設定の場合はエラー画面へ遷移させる
        if (!this.props.location.state || !this.props.location.state.nextUrl) {
            this.props.navigate('/admin/error')
            return
        }

        try {
            // 管理者ユーザ取得
            const adminUserResponse: Nullable<NatGetAdminUser> = await this.props.asyncCallFunction(
                this.props.adminUserRepo.getMyAdminUser.bind(this.props.adminUserRepo),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (adminUserResponse && adminUserResponse.details) {
                this.setState({version: adminUserResponse.details.version})
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    // パスワード入力イベント
    handlePasswordInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
        let password: string = this.state.password
        let passwordConfirm: string = this.state.passwordConfirm

        switch (event.target.name) {
            case 'password':
                password = event.target.value
                break
            case 'passwordConfirm':
                passwordConfirm = event.target.value
                break
            default:
                break
        }

        this.setState({password: password, passwordConfirm: passwordConfirm})
    }

    // キャンセルボタン押下時
    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    // OKボタン押下時
    async handleRegisterOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()

        try {
            const requestParam: UpdateAdminUserPasswordParams = {
                password: this.state.password,
                passwordConfirm: this.state.passwordConfirm,
                version: this.state.version,
            }

            const registerMessageResponse: Nullable<NatPostAdminUser> = await this.props.asyncCallFunction(
                this.props.adminUserRepo.changeAdminUserPassword.bind(this.props.adminUserRepo, requestParam),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0007.toString('パスワード')},
            )

            if (registerMessageResponse && registerMessageResponse.requestError === null) {
                this.props.navigate(
                    this.props.location.state.nextUrl,
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    };

    // 登録ボタン押下
    async registerAdminUserPasswordButton(): Promise<void> {

        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0010.errorLevel,
                message: Messages.IOPCC0010.toString('パスワード'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick.bind(this),
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick.bind(this),
                    },
                ],
            },
        )
    }


    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        const changeButtonDisabled = this.state.password === '' || this.state.passwordConfirm === ''

        const spMode = this.context.showSpModePage

        return (
            <>
                <Box mb={'16px'}/>
                <HeadingAreaMessageComponent eventEmitter={this.props.eventEmitter}/>
                <ExplainArea>
                    <span>初回ログイン時に使用したパスワードを、ご自身用の新しいパスワードに変更してください。</span>
                    <span>新しいパスワードは、今後ログイン時に使用します。</span>
                </ExplainArea>
                <NoteArea>
                    新しいパスワードは他人から推測されにくい組み合わせで設定してください。
                    <ListGridContainer>
                        <div>・</div>
                        <div>半角の英字と数字をそれぞれ1文字以上含む※記号は任意</div>
                        <div>・</div>
                        <div>8文字以上20文字以下</div>
                    </ListGridContainer>
                </NoteArea>
                <MainArea id={'password-form'} $spMode={spMode}>
                    <FlexGridArea area={'passwordLabel'}>
                        <RowTitle>
                            新しいパスワード
                        </RowTitle>
                    </FlexGridArea>
                    <FlexGridArea area={'passwordInput'}>
                        <PasswordInput
                            $spMode={spMode}
                            type="password"
                            name="password"
                            inputProps={{
                                maxLength: 20,
                                ref: this.password,
                            }}
                            autoComplete={'new-password'}
                            eventKeys={['password']}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handlePasswordInputChange.bind(this)}
                            autoFocus
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'passwordConfirmLabel'}>
                        <RowTitle>
                            新しいパスワード（確認用）
                        </RowTitle>
                    </FlexGridArea>
                    <FlexGridArea area={'passwordConfirmInput'}>
                        <PasswordInput
                            $spMode={spMode}
                            type="password"
                            name="passwordConfirm"
                            inputProps={{
                                maxLength: 20,
                                ref: this.passwordConfirm,
                            }}
                            placeholder={'確認のため再度入力してください'}
                            autoComplete={'new-password'}
                            eventKeys={['passwordConfirm']}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handlePasswordInputChange.bind(this)}
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'changePasswordButton'} justifyContent={!spMode ? 'right' : undefined}>
                        <ChangeButton
                            $spMode={spMode}
                            onClick={this.registerAdminUserPasswordButton.bind(this)}
                            disabled={changeButtonDisabled}>
                            パスワードを変更する
                        </ChangeButton>
                    </FlexGridArea>
                </MainArea>
            </>
        )
    }
}

NatAdminChangePasswordInputPage.contextType = AppAdminContext
export default createAdminPage(NatAdminChangePasswordInputPage)

const ExplainArea = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
`

const NoteArea = styled.div`
  margin-top: 16px;
  padding: 8px;
  background-color: #E5E5E5;
  font-size: 12px;
`

const MainArea = styled.div<{ $spMode: boolean }>`
  background-color: ${Colors.Surface};
  display: grid;
  ${props => props.$spMode ? `
  margin: 16px -15px;
  padding: 16px;
  grid-template:
    "passwordLabel"
    "."                     5px
    "passwordInput"
    "."                     16px
    "passwordConfirmLabel"
    "."                     5px
    "passwordConfirmInput"
    "."                     32px
    "changePasswordButton"
    "."                     16px
     / 1fr;
  ` : `
  margin-top: 16px;
  padding: 20px 40px 24px;
  grid-template:
    "passwordLabel        passwordInput"
    ".                    .            " 32px
    "passwordConfirmLabel passwordConfirmInput"
    ".                    .            " 24px
     ".                   changePasswordButton"
      / max-content 1fr;
  column-gap: 40px;
  `}
`

const RowTitle = styled.div`
  font-size: 16px;
  color: ${Colors.AccentDark};
`

const PasswordInput = styled(PPTextField)<{ $spMode: boolean }>`
  ${props => props.$spMode ? `
  width: 100%;
  ` : `
  width: 480px;
  `}
`
const ChangeButton = styled(AdminPrimaryButton)<{ $spMode: boolean }>`
  ${props => props.$spMode && `
  width: 100%;
  `}
`
